// @flow
/**
 * Actions for interacting with API resource.
 *
 * @todo 18-08-13 ADD TESTS
 */

import _invariant            from 'invariant';
import { createAction }      from 'redux-actions';
import Identifier            from '../modules/editableContent/domain/Identifier';
import type { ThunkDetails } from '../modules/redux-saga-thunk/redux-saga-thunk.types';
import {
  METHOD_PATCH,
  METHOD_POST,
  METHOD_PUT,
}                            from './utils/apiResources/apiResourceActions.const';


export const KEY_RESOURCE_IRI = 'resourceIRI';


// -----------------------------------------------------------------------------------------------------------------
// Generic types
// -----------------------------------------------------------------------------------------------------------------

type ActionCreator<Args, Action, ActionType: string> = {|
  (...Args): Action,
  toString: () => ActionType,
|}


type ResultActionCreatorArgs<RequestArgs, RequestType> = {|
  ...RequestArgs,
  uuid: Identifier,
  thunk: ThunkDetails<RequestType>,
  ignoreThunk?: boolean,
|}


// -----------------------------------------------------------------------------------------------------------------
// Reset action
// -----------------------------------------------------------------------------------------------------------------

type ResetApiResourcesType = 'API:RESOURCES:RESET';

type ResetAction = {
  type: ResetApiResourcesType,
};

export const resetApiResources: ActionCreator<[], ResetAction, ResetApiResourcesType> = createAction(
  'API:RESOURCES:RESET',
);


// -----------------------------------------------------------------------------------------------------------------
// Fetch actions
// -----------------------------------------------------------------------------------------------------------------

// Fetch Request
// -------------

type FetchRequestCreatorArgs = {|
  resourceIRI: string,
|}

type FetchRequestPayload = {|
  uuid: Identifier,
  resourceIRI: string,
|}

type FetchRequestMeta<ThunkType: true | ThunkDetails<string>> = {|
  thunk: ThunkType,
|}

type FetchRequestType = 'API:RESOURCE:FETCH:REQUEST'

export type FetchRequestAction<ThunkType> = {|
  type: FetchRequestType,
  payload: FetchRequestPayload,
  meta: FetchRequestMeta<ThunkType>,
|}

export const fetchApiResourceRequest: ActionCreator<[FetchRequestCreatorArgs], FetchRequestAction<true>, FetchRequestType> = createAction(
  'API:RESOURCE:FETCH:REQUEST',
  ({resourceIRI}: FetchRequestCreatorArgs): FetchRequestPayload => {

    _invariant(resourceIRI, 'Missing `resourceIRI`');

    return {uuid: new Identifier(), resourceIRI};
  },
  (): FetchRequestMeta<true> => ({thunk: true}),
);


// Fetch Success
// -------------

type FetchSuccessCreatorArgs = {|
  ...ResultActionCreatorArgs<FetchRequestCreatorArgs, FetchRequestType>,
  receivedData: Object
|}

type FetchSuccessPayload = {|
  receivedData: Object
|}

type FetchSuccessMeta = {|
  ...$Diff<FetchSuccessCreatorArgs, FetchSuccessPayload>,
  ignoreThunk?: boolean,
|}

type FetchSuccessType = 'API:RESOURCE:FETCH:SUCCESS'

export type FetchSuccessAction = {|
  type: FetchSuccessType,
  payload: FetchSuccessPayload,
  meta: FetchSuccessMeta,
|}

export const fetchApiResourceSuccess: ActionCreator<[FetchSuccessCreatorArgs], FetchSuccessAction, FetchSuccessType> = createAction(
  'API:RESOURCE:FETCH:SUCCESS',
  ({receivedData}: FetchSuccessCreatorArgs): FetchSuccessPayload => {

    _invariant(receivedData, 'Missing `receivedData`');

    return {receivedData};
  },
  ({uuid, resourceIRI, thunk, ignoreThunk = false}: FetchSuccessCreatorArgs): FetchSuccessMeta => {

    _invariant(uuid, 'Missing `uuid`');
    _invariant(resourceIRI, 'Missing `resourceIRI`');
    _invariant(ignoreThunk || thunk, 'Missing `thunk`');

    return {uuid, resourceIRI, thunk};
  },
);

// Fetch Failure
// -------------

type FetchFailureCreatorArgs = {|
  ...ResultActionCreatorArgs<FetchRequestCreatorArgs, FetchRequestType>,
|};

type FetchFailureMeta = {|
  ...FetchFailureCreatorArgs,
|}

type FetchFailureType = 'API:RESOURCE:FETCH:FAILURE'

type FetchFailureAction = {|
  type: FetchFailureType,
  payload: Error,
  meta: FetchRequestPayload,
|}

export const fetchApiResourceFailure: ActionCreator<[Error, FetchFailureCreatorArgs], FetchFailureAction, FetchFailureType> = createAction(
  'API:RESOURCE:FETCH:FAILURE',
  null,
  (error: Error, {uuid, resourceIRI, thunk}: FetchFailureCreatorArgs): FetchFailureMeta => {

    _invariant(uuid, 'Missing `uuid`');
    _invariant(resourceIRI, 'Missing `resourceIRI`');
    _invariant(thunk, 'Missing `thunk`');

    return {uuid, resourceIRI, thunk};
  },
);


// -----------------------------------------------------------------------------------------------------------------
// Save actions
// -----------------------------------------------------------------------------------------------------------------

// Save request
// ------------

export type SaveRequestType = 'API:RESOURCE:SAVE:REQUEST';

type SaveRequestCreatorArgs = {|
  resourceIRI: string,
  method: METHOD_POST | METHOD_PATCH | METHOD_PUT,
  dataToSave: {},
|}

export type SaveRequestPayload = {|
  ...SaveRequestCreatorArgs,
  uuid: Identifier,
|}

// We need to support `true` and ThunkDetails<SaveRequestType>>
export type SaveRequestMeta<ThunkType: true | ThunkDetails<SaveRequestType>> = {|
  thunk: ThunkType,
|}

type BaseSaveRequestAction<ThunkType> = {|
  type: SaveRequestType,
  payload: SaveRequestPayload,
  meta: SaveRequestMeta<ThunkType>,
|};

export type SaveRequestActionBeforeDispatch = BaseSaveRequestAction<true>;
export type SaveRequestActionAfterDispatch = BaseSaveRequestAction<ThunkDetails<SaveRequestType>>;

/**
 * @type {function({resourceIRI: string, method: string, dataToSave: object, thunk: boolean}): object}
 */
export const saveApiResourceRequest: ActionCreator<SaveRequestCreatorArgs, SaveRequestActionBeforeDispatch, SaveRequestType> = createAction(
  'API:RESOURCE:SAVE:REQUEST',
  ({resourceIRI, method, dataToSave}: SaveRequestCreatorArgs): SaveRequestPayload => {

    _invariant(resourceIRI, 'Missing `resourceIRI`');
    _invariant(method, 'Missing `method`');
    _invariant(dataToSave, 'Missing `dataToSave`');

    return {uuid: new Identifier(), resourceIRI, method, dataToSave};

  },
  (): SaveRequestMeta<true> => ({thunk: true}),
);

// Save success
// ------------

type SaveSuccessCreatorArgs = {|
  ...ResultActionCreatorArgs<SaveRequestCreatorArgs, SaveRequestType>,
  receivedData: {},
|}

type SaveSuccessPayload = {|
  receivedData: {},
|}

type SaveSuccessMeta = {|
  ...$Diff<SaveSuccessCreatorArgs, SaveSuccessPayload>,
  thunk: ThunkDetails<SaveRequestType>,
|}

type SaveSuccessType = 'API:RESOURCE:SAVE:SUCCESS';

export type SaveSuccessAction = {
  type: SaveSuccessType,
  payload: SaveSuccessPayload,
  meta: SaveSuccessMeta,
};

/**
 * @type {function({uuid: Identifier, resourceIRI: string, method: string, dataToSave: object, receivedData: object}):
 *   object}
 */
export const saveApiResourceSuccess: ActionCreator<[SaveSuccessCreatorArgs], SaveSuccessAction, SaveSuccessType> = createAction(
  'API:RESOURCE:SAVE:SUCCESS',
  ({receivedData}: SaveSuccessCreatorArgs): SaveSuccessPayload => {

    _invariant(receivedData, 'Missing `receivedData`');

    return {receivedData};

  },
  ({uuid, resourceIRI, method, dataToSave, thunk}: SaveSuccessCreatorArgs): SaveSuccessMeta => {

    _invariant(uuid, 'Missing `uuid`');
    _invariant(
      uuid instanceof Identifier,
      '`uuid` should be the Identifier object set on the original request action.',
    );
    _invariant(resourceIRI, 'Missing `resourceIRI`');
    _invariant(method, 'Missing `method`');
    _invariant(dataToSave, 'Missing `dataToSave`');

    return {uuid, resourceIRI, method, dataToSave, thunk};

  },
);


// Save failure
// ------------

type SaveFailureCreatorArgs = {|
  ...ResultActionCreatorArgs<SaveRequestCreatorArgs, SaveRequestType>,
|};

type SaveFailureMeta = SaveFailureCreatorArgs;

type SaveFailureType = 'API:RESOURCE:SAVE:FAILURE';

type SaveFailureAction = {|
  type: SaveFailureType,
|};

/**
 * @type {function(error: Error, {uuid: Identifier, resourceIRI: string, method: string, dataToSave: object})}
 */
export const saveApiResourceFailure: ActionCreator<[Error, SaveFailureCreatorArgs], SaveFailureAction, SaveFailureType> = createAction(
  'API:RESOURCE:SAVE:FAILURE',
  null,
  (error: Error, {uuid, resourceIRI, method, dataToSave, thunk}: SaveFailureCreatorArgs): SaveFailureMeta => {

    _invariant(error, 'Missing `error`');
    _invariant(error instanceof Error, '`error` should be an instance of `Error`.');
    _invariant(uuid, 'Missing `uuid`');
    _invariant(
      uuid instanceof Identifier,
      '`uuid` should be the Identifier object set on the original request action.');
    _invariant(resourceIRI, 'Missing `resourceIRI`');
    _invariant(method, 'Missing `method`');
    _invariant(dataToSave, 'Missing `dataToSave`');

    return {uuid, resourceIRI, method, dataToSave, thunk};
  },
);


// -----------------------------------------------------------------------------------------------------------------
// Delete actions
// -----------------------------------------------------------------------------------------------------------------

// Delete request
// --------------

type DeleteRequestType = 'API:RESOURCE:DELETE:REQUEST';

type DeleteRequestCreatorArgs = {|
  resourceIRI: string,
|}

type DeleteRequestPayload = {|
  uuid: Identifier,
  ...DeleteRequestCreatorArgs,
|}

type DeleteRequestMeta<ThunkType: true | ThunkDetails<DeleteRequestType>> = {|
  thunk: ThunkType
|}

type DeleteRequestAction<ThunkType> = {|
  type: DeleteRequestType,
  payload: DeleteRequestPayload,
  meta: DeleteRequestMeta<ThunkType>,
|};

export type DeleteRequestActionBeforeDispatch = DeleteRequestAction<true>;
export type DeleteRequestActionAfterDispatch = DeleteRequestAction<ThunkDetails<DeleteRequestType>>;

/**
 * @type {function({resourceIRI: string, dataToDelete: object, thunk: boolean}): object}
 */
export const deleteApiResourceRequest: ActionCreator<[DeleteRequestCreatorArgs], DeleteRequestActionBeforeDispatch, DeleteRequestType> = createAction(
  'API:RESOURCE:DELETE:REQUEST',
  ({resourceIRI}: DeleteRequestCreatorArgs): DeleteRequestPayload => {

    _invariant(resourceIRI, 'Missing `resourceIRI`');

    return {uuid: new Identifier(), resourceIRI};

  },
  (): DeleteRequestMeta<true> => ({thunk: true}),
);

// Delete success
// --------------

type DeleteSuccessCreatorArgs = {|
  ...ResultActionCreatorArgs<DeleteRequestCreatorArgs, DeleteRequestType>,
|}

type DeleteSuccessMeta = {|
  ...DeleteSuccessCreatorArgs,
  thunk: ThunkDetails<DeleteRequestType>,
|}

type DeleteSuccessType = 'API:RESOURCE:DELETE:SUCCESS';

type DeleteSuccessAction = {
  type: DeleteSuccessType,
  payload: null,
  meta: DeleteSuccessMeta,
};

/**
 * @type {function({uuid: Identifier, resourceIRI: string, method: string, dataToDelete: object, receivedData: object}):
 *   object}
 */
export const deleteApiResourceSuccess: ActionCreator<[DeleteSuccessCreatorArgs], DeleteSuccessAction, DeleteSuccessType> = createAction(
  'API:RESOURCE:DELETE:SUCCESS',
  null,
  ({uuid, resourceIRI, thunk}: DeleteSuccessCreatorArgs): DeleteSuccessMeta => {

    _invariant(uuid, 'Missing `uuid`');
    _invariant(
      uuid instanceof Identifier,
      '`uuid` should be the Identifier object set on the original request action.',
    );
    _invariant(resourceIRI, 'Missing `resourceIRI`');

    return {uuid, resourceIRI, thunk};

  },
);


// Delete failure
// --------------

type DeleteFailureType = 'API:RESOURCE:DELETE:FAILURE';

type DeleteFailureCreatorArgs = {|
  ...ResultActionCreatorArgs<DeleteRequestCreatorArgs, DeleteRequestType>,
|};

type DeleteFailureMeta = {|
  ...DeleteFailureCreatorArgs,
  thunk: ThunkDetails<DeleteRequestType>,
|}

type DeleteFailureAction = {|
  type: DeleteFailureType,
|};

/**
 * @type {function(error: Error, {uuid: Identifier, resourceIRI: string, method: string, dataToDelete: object})}
 */
export const deleteApiResourceFailure: ActionCreator<[Error, DeleteFailureCreatorArgs], DeleteFailureAction, DeleteFailureType> = createAction(
  'API:RESOURCE:DELETE:FAILURE',
  null,
  (error: Error, {uuid, resourceIRI, thunk}: DeleteFailureCreatorArgs): DeleteFailureMeta => {

    _invariant(error, 'Missing `error`');
    _invariant(error instanceof Error, '`error` should be an instance of `Error`.');
    _invariant(uuid, 'Missing `uuid`');
    _invariant(
      uuid instanceof Identifier,
      '`uuid` should be the Identifier object set on the original request action.');
    _invariant(resourceIRI, 'Missing `resourceIRI`');

    return {uuid, resourceIRI, thunk};
  },
);



// WEBPACK FOOTER //
// ./src/actions/apiResourceActions.js